import { IContextualMenuItem, IContextualMenuItemProps, IRenderFunction } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { officeFileType, OneNoteProgId } from "../../../src/taskpane/config/constants";
import { ISearchOpenFileInfo } from "../../../src/taskpane/interfaces/ISearchOpenFileInfo";
import { RootState } from "../../../src/taskpane/redux";
import { getFileItemIdFromRelativePath, getItemCustomSelect, getItemWebViewUrl, getListItemWebUrl } from "../../../src/taskpane/services/GraphService";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { brandedCommandbarIconStyles, menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { OneDriveUrlHelper } from "../../../src/taskpane/components/helpers/oneDriveUrlHelper";
import { logger } from "../../../utilities/logger";
import { getGridItemUrl } from "../../../src/taskpane/components/helpers/UrlHelper";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { DocumentArrowRight20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/DocumentArrowRight20Regular";
import { DocumentGlobe20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/DocumentGlobe20Regular";
import { Open20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/Open20Regular";
import { OpenFolder20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/OpenFolder20Regular";

const getItemId = async (selectedItem: any, driveId: string) => {
    let itemId: string;
    try {
        const spItemUrlparts = selectedItem[".spItemUrl"]?.split("/");
        if (Array.isArray(spItemUrlparts) && spItemUrlparts.length > 0) {
            itemId = spItemUrlparts[spItemUrlparts.length - 1]?.split("?")[0];
        }

        if (!itemId) {
            const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
            const folderUrl = new URL(driveUrl.origin + selectedItem.FileRef);
            itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
        }
    } catch (error) {
        // Ignore
    }
    return itemId;
};

export function useMenuItem_Open(selectedItems: any[], waitFor: boolean, setWaitFor: (waitFor: boolean) => void, isInOneDrive: boolean, selectedItemInfo: ISearchOpenFileInfo) {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const allowOpenInSharePoint = useSelector((state: RootState) => (state.user.tenantAllowOpenInSharePoint ?? true) && !state.workingDirectories.workingDirectories?.some(dir => dir?.type === ComponentTypes.Docubird));
    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const userAccount = useSelector((state: RootState) => state.user.account);
    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);

    const getOpenButtonItems = React.useCallback((selectedItem: any, isInOneDrive: boolean, selectedItemInfo: ISearchOpenFileInfo): IContextualMenuItem[] => {
        const isFolder: boolean = selectedItem?.FSObjType === "1" && selectedItem?.ProgId !== OneNoteProgId;

        const browser: IContextualMenuItem = {
            key: "browser",
            text: isFolder
                ? !isInOneDrive ? `${languageStrings.OpenIn} SharePoint` : `${languageStrings.OpenIn} OneDrive`
                : `${languageStrings.OpenIn} ${languageStrings.Browser}`,
            // iconProps: { iconName: !isFolder ? "BrowserTab" : !isInOneDrive ? "SharepointLogo" : "OneDriveLogo" },
            iconProps: {},
            onClick: () => {
                (async () => {
                    if (selectedItem?.File_x0020_Type === "url") {
                        const itemUrl: string = selectedItem._ShortcutUrl ?? CacheHelper.get(`shortcutLink_${selectedItem.UniqueId?.replace("{", "").replace("}", "")}`);
                        if (itemUrl) {
                            const url = await getGridItemUrl(selectedItem, itemUrl);
                            window.open(url, "_blank");
                            return;
                        }
                    }

                    setWaitFor(true);
                    try {
                        const item = selectedItem;
                        const driveId = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;

                        const spItemUrlparts = item['.spItemUrl'].split('/');
                        let itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

                        if (!itemId) {
                            const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
                            const folderUrl = new URL(driveUrl.origin + item.FileRef);
                            itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
                        }

                        // Selecting sharepointIds together with webUrl because in some cases graph returns more suitable Office web app url formats.
                        const fileItem = await getItemCustomSelect(await authModule.getGraphAccessToken(), driveId, itemId, "webUrl,sharepointIds");
                        const url = fileItem?.webUrl || await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, itemId);
                        window.open(url, "_blank");
                    } catch (error) {
                        logger(error, "useMenuItem_Open.tsx (open in browser)");
                    }
                    setWaitFor(false);
                })();
            },
            onRenderIcon: (props: IContextualMenuItemProps, defaultRender?: IRenderFunction<IContextualMenuItemProps>) => {
                return selectedItemInfo && selectedItemInfo.iconSrc
                    ? <img src={selectedItemInfo.iconSrc} className={brandedCommandbarIconStyles} />
                    // : defaultRender({ ...props });
                    : (!isFolder
                        ? <DocumentGlobe20Regular className={menuItemIconStyles} />
                        : <OpenFolder20Regular className={menuItemIconStyles} />);
            },
        };

        const app: IContextualMenuItem = selectedItemInfo && selectedItem?.EffectiveBasePermissions?.canOpenItems && (!currentSharepointFolder.disableOpenInApp || selectedItemInfo.isOfficeFile) && selectedItem?.File_x0020_Type !== "url" && {
            key: "app",
            text: `${languageStrings.OpenIn} ${languageStrings.App}`,
            // iconProps: { iconName: "CloneToDesktop" },
            iconProps: {},
            onClick: () => {
                (async () => {
                    setWaitFor(true);
                    try {
                        let openSelf: boolean = true;
                        let url: string;
                        if (!selectedItemInfo.isOfficeFile) {
                            const fileId: string = selectedItem.UniqueId?.replace("{", "").replace("}", "");
                            const fileName: string = selectedItem.FileLeafRef;
                            const userEmail: string = userAccount.username;
                            const userId: string = userAccount.uniqueId;

                            let siteId: string;
                            let listId: string;
                            let webUrl: string;
                            if (!isInOneDrive) {
                                const siteIdParts: string[] = currentDirectory.siteId?.split(","); // xblox.sharepoint.com,058798cc-59c1-451f-8957-49e32c49aeb1,4c5fa046-b695-4b4b-86ef-b47bb86f7cbe
                                if (Array.isArray(siteIdParts) && siteIdParts.length > 1 && siteIdParts[1]) {
                                    siteId = siteIdParts[1];
                                }

                                listId = currentSharepointFolder?.sharepointListId ?? currentDirectory?.sharePointListId;
                                webUrl = currentDirectory?.webUrl;
                            }

                            if (!siteId || !listId || !webUrl) {
                                const driveId: string = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
                                let itemId: string = await getItemId(selectedItem, driveId);

                                const file = driveId && itemId && await getItemCustomSelect(await authModule.getGraphAccessToken(), driveId, itemId, "sharepointIds");
                                if (file?.sharepointIds) {
                                    siteId = file.sharepointIds.siteId;
                                    listId = file.sharepointIds.listId;
                                    webUrl = file.sharepointIds.siteUrl;
                                }
                            }

                            url = OneDriveUrlHelper.getOpenInOneDriveDesktopUrl(fileId, siteId, listId, userEmail, userId, webUrl, fileName);
                        }
                        else {
                            const driveId: string = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
                            let itemId: string = await getItemId(selectedItem, driveId);
                            const fileWebUrl: string = await getListItemWebUrl(await authModule.getGraphAccessToken(), driveId, itemId);
                            url = `${selectedItemInfo.localUrlPrepended}${fileWebUrl}`;

                            if (!selectedItemInfo.localUrlPrepended) {
                                openSelf = false;
                            }
                        }

                        window.open(url, openSelf ? "_self" : "_blank");
                    } catch (error) {
                        logger(error, "useMenuItem_Open.tsx (open in app)");
                    }
                    setWaitFor(false);
                })();
            },
            onRenderIcon: (props: IContextualMenuItemProps, defaultRender?: IRenderFunction<IContextualMenuItemProps>) => {
                return selectedItemInfo && selectedItemInfo.iconSrc
                    ? <img src={selectedItemInfo.iconSrc} className={brandedCommandbarIconStyles} />
                    // : defaultRender({ ...props });
                    : <DocumentArrowRight20Regular className={menuItemIconStyles} />;
            },
        };

        let openOptions: IContextualMenuItem[];
        if (selectedItem?.EffectiveBasePermissions?.canEditListItem || selectedItem?.EffectiveBasePermissions?.canOpenItems) {
            if (selectedItemInfo?.type == officeFileType.Browser) {
                openOptions = allowOpenInSharePoint === false
                    ? []
                    : [browser];
            }
            else {
                openOptions = [browser, app].filter(x => x);
            }
        }
        return openOptions ?? [];
    }, [languageStrings, setWaitFor, currentDirectory, oneDriveRootDetails, userAccount, currentSharepointFolder, allowOpenInSharePoint]);

    const menuItem_Open = React.useMemo(() => {
        if (selectedItems?.length === 1 && !(selectedItemInfo?.type == officeFileType.Browser && allowOpenInSharePoint === false) && currentDirectory.type !== ComponentTypes.SharePointList) {
            const subMenuItems: IContextualMenuItem[] = getOpenButtonItems(selectedItems[0], isInOneDrive, selectedItemInfo);
            if (subMenuItems?.length > 0) {
                return {
                    key: 'open',
                    disabled: waitFor === true || isBusySaving === true,
                    name: languageStrings.Open,
                    // iconProps: { iconName: "FolderOpen" },
                    iconProps: {},
                    onRenderIcon: (props: IContextualMenuItemProps, defaultRender?: IRenderFunction<IContextualMenuItemProps>) => {
                        return selectedItemInfo?.iconSrc
                            ? <img src={selectedItemInfo.iconSrc} style={{ marginLeft: 4, marginRight: 4, width: 16, height: 16 }} />
                            // : defaultRender({ ...props });
                            : <Open20Regular className={menuItemIconStyles} />;
                    },
                    subMenuProps: {
                        items: subMenuItems
                    }
                } as IContextualMenuItem;
            }
        }
        return null;
    }, [selectedItems, waitFor, isInOneDrive, selectedItemInfo, isBusySaving, allowOpenInSharePoint, currentDirectory, getOpenButtonItems]);

    return {
        menuItem_Open
    }

}
